import { Button, Checkbox, I, Input, Row, Stack } from '@leanin/ui';
import { useTranslations } from 'use-intl';
import {
  MAX_CHOICE_OPTION_CHARS,
  MAX_CHOICE_OPTIONS,
  MIN_CHOICE_OPTIONS,
} from '@leanin/shared/intake';
import type { DraftQuestion } from './intake-builder-model';

type Props = {
  readonly question: DraftQuestion;
  readonly onPatch: (patch: Partial<DraftQuestion>) => void;
};

// The type-specific controls revealed beneath a question card's top row while
// it is being edited in place: the single-choice option list (with an inert
// radio preview matching the applicant's radio group) and the Required footer.
// The question wording and answer-type selector live in the card's top row, not
// here, so the wording is never shown twice.
export function QuestionEditor({ question, onPatch }: Props) {
  const t = useTranslations('Group');

  const setOption = (index: number, value: string) => {
    onPatch({
      options: question.options.map((o, i) => (i === index ? value : o)),
    });
  };
  const addOption = () => {
    if (question.options.length >= MAX_CHOICE_OPTIONS) return;
    onPatch({ options: [...question.options, ''] });
  };
  const removeOption = (index: number) => {
    onPatch({ options: question.options.filter((_, i) => i !== index) });
  };

  return (
    <Stack gap="md">
      {question.type === 'single_choice' && (
        <Stack gap="sm">
          {question.options.map((option, index) => (
            <Row key={index} gap="sm" align="center">
              {/* Inert radio-circle preview mirroring the applicant's radio group. */}
              <span
                aria-hidden
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: '1.75px solid var(--border-strong)',
                  flexShrink: 0,
                }}
              />
              <Input
                value={option}
                maxLength={MAX_CHOICE_OPTION_CHARS}
                placeholder={t('admin.joinQuestions.options.placeholder', {
                  n: index + 1,
                })}
                onChange={(e) => setOption(index, e.target.value)}
                style={{ flex: 1 }}
              />
              <Button
                variant="ghost"
                size="sm"
                icon={I.x}
                aria-label={t('admin.joinQuestions.options.remove')}
                disabled={question.options.length <= MIN_CHOICE_OPTIONS}
                onClick={() => removeOption(index)}
              />
            </Row>
          ))}
          <Row>
            <Button
              variant="ghost"
              size="sm"
              icon={I.plus}
              disabled={question.options.length >= MAX_CHOICE_OPTIONS}
              onClick={addOption}
            >
              {t('admin.joinQuestions.options.add')}
            </Button>
          </Row>
        </Stack>
      )}
      <Checkbox
        checked={question.required}
        onCheckedChange={(checked) => onPatch({ required: checked === true })}
        label={t('admin.joinQuestions.question.requiredLabel')}
      />
    </Stack>
  );
}
